/**
 * findMatchingPresetId — pure utility extracted from ThemeSettings.tsx
 *
 * Finds which preset (if any) exactly matches the saved org theme. Used on
 * initial load to highlight the active preset card so returning to the
 * Design System tab shows "this is the theme your org is currently using".
 *
 * Matches on the fields a user can influence via the preset picker:
 * colour palette, typography families, and border radius.
 *
 * Normalisation applied before comparison:
 *   - null / undefined / empty-string → treated as equivalent (all map to null)
 *   - Hex colours are compared case-insensitively (#1E40AF === #1e40af)
 *   - Font-family strings are trimmed of leading/trailing whitespace
 */

import type { ThemePreset } from '@/components/DesignSystem/ThemePresetCard';

/** Keys compared when looking for an exact preset match. */
export const PRESET_MATCH_KEYS: ReadonlyArray<keyof ThemePreset> = [
  'primaryColor', 'secondaryColor', 'accentColor',
  'successColor', 'warningColor', 'errorColor',
  'infoColor', 'grayColor',
  'headingFontFamily', 'bodyFontFamily',
  'borderRadius',
];

/**
 * Normalise a raw theme field value for comparison.
 *
 * Rules:
 *   1. null / undefined / '' → null (all treated as "unset")
 *   2. Any string value is trimmed of leading/trailing whitespace
 *   3. Hex colour strings are lowercased (#1E40AF → #1e40af)
 */
export function normThemeValue(v: unknown): string | null {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  if (s === '') return null;
  // Lowercase hex colors so #1E40AF and #1e40af are treated as identical
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
  return s;
}

/**
 * Return the id of the first preset whose 11 comparison keys all match
 * the org theme, or null if no preset matches.
 */
export function findMatchingPresetId(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  theme: Record<string, any> | null | undefined,
  candidates: ThemePreset[],
): string | null {
  if (!theme || candidates.length === 0) return null;
  for (const preset of candidates) {
    const allMatch = PRESET_MATCH_KEYS.every(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      k => normThemeValue((preset as any)[k]) === normThemeValue(theme[k]),
    );
    if (allMatch) return preset.id;
  }
  return null;
}
